import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CalendarClock, Download, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Checkbox } from '@quebon/ui/components/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Spinner } from '@quebon/ui/components/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@quebon/ui/components/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { cn } from '@quebon/ui/lib/utils'
import { saveBlob } from '@/api/csvDownload'
import { printBookStatusLabel } from '@/api/printBook'
import {
  textbookProgressBoardApi,
  textbookProgressBoardQueryKeys,
  type TextbookProgressBoardRow,
  type TextbookProgressBook,
  type TextbookProgressClass,
  type TextbookProgressEntitlement,
  type TextbookProgressFrontrunner,
} from '@/api/textbookProgressBoard'
import { LearnerProgressPage } from './LearnerProgressPage'

/**
 * 교재 진도 현황판 (한시) — 코스별 선두 원생의 현재/다음 교재.
 * 교재 제작이 끝나면 이 페이지·탭·API를 제거한다. docs/features/admin-textbook-progress-board/STATE.md
 */

const BOOK_STATUS_CLASS: Record<string, string> = {
  draft: 'border-amber-300 bg-amber-50 text-amber-800',
  ready: 'border-sky-300 bg-sky-50 text-sky-800',
  completed: 'border-emerald-300 bg-emerald-50 text-emerald-800',
}

const DAY_LABEL: Record<string, string> = {
  mon: '월',
  tue: '화',
  wed: '수',
  thu: '목',
  fri: '금',
  sat: '토',
  sun: '일',
}

const GATE_LABEL: Record<string, string> = {
  NONE: '수강권 없음',
  NOT_STARTED: '미시작',
  EXHAUSTED: '소진',
  EXPIRED: '만료',
  ORG_MISMATCH: '센터 불일치',
}

function formatKstDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function formatKstDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' })
}

function bookLabel(volumeLabel: string | null, title: string): string {
  return volumeLabel && volumeLabel.trim() ? volumeLabel : title
}

function BookStatusBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className={cn('font-normal', BOOK_STATUS_CLASS[status])}>
      {printBookStatusLabel(status)}
    </Badge>
  )
}

function CurrentBookCell({ book, hidden }: { book: TextbookProgressBook | null; hidden: boolean }) {
  if (hidden) return <span className="text-slate-400">-</span>
  if (!book) {
    return <Badge className="bg-red-600 text-white hover:bg-red-600">교재 없음</Badge>
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-medium" title={book.title}>{bookLabel(book.volumeLabel, book.title)}</span>
        <BookStatusBadge status={book.status} />
      </div>
      <span className="text-xs text-slate-500">
        권 내 {book.currentLessonRank}/{book.totalLessons}
      </span>
    </div>
  )
}

function NextBookCell({ book, hidden }: { book: TextbookProgressBook | null; hidden: boolean }) {
  if (hidden || !book) return <span className="text-slate-400">-</span>
  const next = book.nextBook
  return (
    <div className="flex flex-col gap-1">
      {next ? (
        <div className="flex items-center gap-2">
          <span className="font-medium" title={next.title}>{bookLabel(next.volumeLabel, next.title)}</span>
          <BookStatusBadge status={next.status} />
        </div>
      ) : (
        <Badge className="bg-red-600 text-white hover:bg-red-600">다음 권 미등록</Badge>
      )}
      <span className={cn('text-xs', book.remainingLessons <= 3 ? 'font-semibold text-red-600' : 'text-slate-500')}>
        {book.remainingLessons}레슨 후 필요
      </span>
    </div>
  )
}

function EntitlementCell({ entitlement }: { entitlement: TextbookProgressEntitlement | null }) {
  if (!entitlement) {
    return <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">수강권 없음</Badge>
  }
  const gate = entitlement.contentFeeGate
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-medium">잔여 {entitlement.remainingLessons}</span>
        <span className="text-xs text-slate-500">
          {entitlement.level} · 부여 {entitlement.grantedLessons} + 이월 {entitlement.carriedOver} − 사용 {entitlement.usedLessons}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span>기간 종료 {entitlement.periodEnd ? formatKstDate(entitlement.periodEnd) : '미시작'}</span>
        {gate && (
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">
            {GATE_LABEL[gate] ?? gate}
          </Badge>
        )}
      </div>
    </div>
  )
}

/** 클래스(timeslot) 시간표 — 요일별 배지. 클래스 미배정·시간표 없음은 문구로. */
function ClassScheduleCell({ cls }: { cls: TextbookProgressClass | null | undefined }) {
  if (!cls) {
    return <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">클래스 미배정</Badge>
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{cls.name}</span>
        {!cls.active && <Badge variant="outline" className="font-normal text-slate-500">비활성</Badge>}
        {cls.teacherName && <span className="text-xs text-slate-500">담당 {cls.teacherName}</span>}
      </div>
      {cls.days.length === 0 ? (
        <span className="text-xs text-slate-400">시간표 없음</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {cls.days.map((d) => (
            <Badge key={d.day} variant="secondary" className="font-normal tabular-nums">
              {DAY_LABEL[d.day] ?? d.day} {d.startTime}~{d.endTime}
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

/** 선두 위치 원생 전원 + 각자의 클래스 시간표. 대표 원생이 첫 행. */
function FrontrunnerLearnersDialog({ row, onClose }: { row: TextbookProgressBoardRow | null; onClose: () => void }) {
  const learners = row?.frontrunnerLearners ?? []
  return (
    <Dialog open={row !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{row?.courseName} 선두 원생 클래스 시간표</DialogTitle>
          <DialogDescription>
            같은 위치에 있는 원생 {learners.length}명입니다. 첫 행이 현황판에 표시된 대표 원생이며, 시간표는 센터 클래스(timeslot)
            설정 기준입니다.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[140px]">원생</TableHead>
                <TableHead className="min-w-[120px]">센터</TableHead>
                <TableHead className="min-w-[260px]">클래스 · 시간표</TableHead>
                <TableHead className="min-w-[140px]">마지막 학습</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {learners.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-slate-500">선두 원생이 없습니다.</TableCell>
                </TableRow>
              )}
              {learners.map((l, index) => (
                <TableRow key={l.userId}>
                  <TableCell className="align-top">
                    <div className="flex flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="font-medium">{l.fullName}</span>
                        {index === 0 && <Badge variant="secondary" className="font-normal">대표</Badge>}
                        {l.testCenter && <Badge variant="outline" className="border-purple-300 text-purple-700">테스트 센터</Badge>}
                        {l.testEntitlement && <Badge variant="outline" className="border-purple-300 text-purple-700">테스트 수강권</Badge>}
                      </div>
                      {l.productName && <span className="text-xs text-slate-500">{l.productName}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">{l.organizationName}</TableCell>
                  <TableCell className="align-top"><ClassScheduleCell cls={l.classSchedule} /></TableCell>
                  <TableCell className="align-top text-xs text-slate-500">
                    {l.lastAccessedAt ? formatKstDateTime(l.lastAccessedAt) : '학습 기록 없음'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ProgressCell({ f, courseName }: { f: TextbookProgressFrontrunner; courseName: string }) {
  if (f.curriculumCompleted) {
    return (
      <div className="flex flex-col gap-1">
        <Badge variant="outline" className="w-fit border-emerald-300 bg-emerald-50 text-emerald-800">커리큘럼 완료</Badge>
        {f.lastAccessedAt && <span className="text-xs text-slate-500">마지막 학습 {formatKstDateTime(f.lastAccessedAt)}</span>}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium">
        {f.currentLessonRank ?? '?'}/{f.courseLessonTotal}
        <span className="ml-2 font-normal text-slate-600">{f.currentLessonTitle ?? ''}</span>
      </span>
      <span className="text-xs text-slate-500">
        {f.currentCourseName && f.currentCourseName !== courseName ? `${f.currentCourseName} · ` : ''}
        {f.currentChapterTitle ?? ''}
        {f.lastAccessedAt ? ` · 마지막 학습 ${formatKstDateTime(f.lastAccessedAt)}` : ' · 학습 기록 없음'}
      </span>
    </div>
  )
}

export function TextbookProgressBoardPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = searchParams.get('view') === 'learners' ? 'learners' : 'courses'
  return (
    <Tabs value={view} onValueChange={value => {
      setSearchParams(previous => {
        const next = new URLSearchParams(previous)
        if (value === 'learners') next.set('view', 'learners')
        else next.delete('view')
        return next
      })
    }} className="gap-4 p-6">
      <TabsList aria-label="교재 진도 현황 보기">
        <TabsTrigger value="courses">코스별 교재 현황</TabsTrigger>
        <TabsTrigger value="learners">전체 원생 진도 현황</TabsTrigger>
      </TabsList>
      <TabsContent value="courses">{view === 'courses' && <CourseProgressBoard />}</TabsContent>
      <TabsContent value="learners">{view === 'learners' && <LearnerProgressPage />}</TabsContent>
    </Tabs>
  )
}

function CourseProgressBoard() {
  const [includeTest, setIncludeTest] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [scheduleRow, setScheduleRow] = useState<TextbookProgressBoardRow | null>(null)
  const params = { includeTest }

  const boardQuery = useQuery({
    queryKey: textbookProgressBoardQueryKeys.board(params),
    queryFn: () => textbookProgressBoardApi.getBoard(params),
    placeholderData: (previous) => previous,
  })

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const { blob, filename } = await textbookProgressBoardApi.exportCsv(params)
      saveBlob(blob, filename)
    } catch {
      toast.error('CSV 다운로드에 실패했습니다.')
    } finally {
      setDownloading(false)
    }
  }

  const board = boardQuery.data

  return (
    <div>
      <Card>
        <CardHeader className="border-b">
          <CardTitle>교재 진도 현황</CardTitle>
          <CardDescription>
            정규 과정(LC-과정A~E)에 속한 코스별로 진도가 가장 앞선 원생(선두 원생)의 현재 교재와 다음 권을 Core → Advanced → Elite 순으로 보여 줍니다. 코어 A(A1~A3)·코어 B(B1~B2)는 분할 코스를 이어 한 진도로 집계합니다.

            교재 제작 기간 동안만 쓰는 한시 화면이며, 열 때마다 실시간으로 계산합니다. 대상은 운영 센터의 재원 원생(휴원·무료체험·테스트
            수강권 제외)입니다. 원생 이름을 누르면 같은 위치 원생 전원의 클래스 시간표를 볼 수 있습니다.
          </CardDescription>
          <CardAction>
            <div className="flex flex-wrap items-center gap-3">
              <label htmlFor="textbook-progress-include-test" className="flex items-center gap-2 text-sm text-foreground">
                <Checkbox
                  id="textbook-progress-include-test"
                  checked={includeTest}
                  onCheckedChange={(checked) => setIncludeTest(checked === true)}
                />
                테스트 센터 포함
              </label>
              <Button type="button" variant="outline" onClick={() => boardQuery.refetch()} disabled={boardQuery.isFetching}>
                <RefreshCw data-icon="inline-start" className={cn(boardQuery.isFetching && 'animate-spin')} />
                새로고침
              </Button>
              <Button type="button" onClick={handleDownload} disabled={downloading || !board}>
                <Download data-icon="inline-start" />
                CSV 다운로드
              </Button>
            </div>
          </CardAction>
        </CardHeader>
        <CardContent className="p-0">
          {boardQuery.isError && (
            <Alert variant="destructive" className="m-4">
              <AlertTitle>불러오기 실패</AlertTitle>
              <AlertDescription>교재 진도 현황을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</AlertDescription>
            </Alert>
          )}
          {boardQuery.isPending && (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          )}
          {board && (
            <>
              <div className="flex flex-wrap items-center gap-4 border-b bg-slate-50 px-4 py-2 text-xs text-slate-600">
                <span>집계 시각 {formatKstDateTime(board.generatedAt)}</span>
                <span>집계 대상 원생 {board.eligibleLearnerCount}명</span>
                <span>코스 {board.rows.length}개</span>
                {board.includeTest && <Badge variant="outline">테스트 센터 포함</Badge>}
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[140px]">코스</TableHead>
                      <TableHead className="min-w-[160px]">선두 원생</TableHead>
                      <TableHead className="min-w-[220px]">진도 현황</TableHead>
                      <TableHead className="min-w-[200px]">현재 체크업북</TableHead>
                      <TableHead className="min-w-[200px]">다음 체크업북</TableHead>
                      <TableHead className="min-w-[200px]">현재 워크북</TableHead>
                      <TableHead className="min-w-[200px]">다음 워크북</TableHead>
                      <TableHead className="min-w-[260px]">학습권(레슨 quota) 현황</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {board.rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="py-10 text-center text-slate-500">
                          집계 대상 원생이 없습니다.
                        </TableCell>
                      </TableRow>
                    )}
                    {board.rows.map((row) => {
                      const f = row.frontrunner
                      return (
                        <TableRow key={row.courseId}>
                          <TableCell className="align-top">
                            <div className="flex flex-col gap-1">
                              <span className="font-semibold">{row.courseName}</span>
                              {row.courseFamily ? (
                                <span className="text-xs text-slate-500" title="분할 코스를 이어 한 진도로 집계합니다">
                                  분할 코스 {row.courseIds?.length ?? 0}개 통합
                                </span>
                              ) : null}
                              <span className="text-xs text-slate-500">{row.curriculumTitles.join(' / ')}</span>
                              <span className="text-xs text-slate-500">원생 {row.learnerCount}명</span>
                            </div>
                          </TableCell>
                          {f ? (
                            <>
                              <TableCell className="align-top">
                                <div className="flex flex-col gap-1">
                                  <div className="flex flex-wrap items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => setScheduleRow(row)}
                                      title="클래스 시간표 보기"
                                      className="inline-flex items-center gap-1 font-medium text-sky-700 underline-offset-2 hover:underline"
                                    >
                                      {f.fullName}
                                      <CalendarClock className="size-3.5" aria-hidden />
                                    </button>
                                    {f.tiedLearnerCount > 1 && (
                                      <Badge variant="secondary" className="font-normal">같은 위치 {f.tiedLearnerCount}명</Badge>
                                    )}
                                    {f.testCenter && <Badge variant="outline" className="border-purple-300 text-purple-700">테스트 센터</Badge>}
                                    {f.testEntitlement && <Badge variant="outline" className="border-purple-300 text-purple-700">테스트 수강권</Badge>}
                                  </div>
                                  <span className="text-xs text-slate-500">{f.organizationName}</span>
                                  {f.productName && <span className="text-xs text-slate-500">{f.productName}</span>}
                                  <span className="text-xs text-slate-500">
                                    {f.classSchedule
                                      ? `${f.classSchedule.name}${f.classSchedule.days.length > 0 ? ` · ${f.classSchedule.days.map((d) => DAY_LABEL[d.day] ?? d.day).join('')}` : ''}`
                                      : '클래스 미배정'}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="align-top"><ProgressCell f={f} courseName={row.courseName} /></TableCell>
                              <TableCell className="align-top"><CurrentBookCell book={f.checkupBook} hidden={f.curriculumCompleted} /></TableCell>
                              <TableCell className="align-top"><NextBookCell book={f.checkupBook} hidden={f.curriculumCompleted} /></TableCell>
                              <TableCell className="align-top"><CurrentBookCell book={f.workbook} hidden={f.curriculumCompleted} /></TableCell>
                              <TableCell className="align-top"><NextBookCell book={f.workbook} hidden={f.curriculumCompleted} /></TableCell>
                              <TableCell className="align-top"><EntitlementCell entitlement={f.entitlement} /></TableCell>
                            </>
                          ) : (
                            <TableCell colSpan={7} className="align-top text-slate-400">선두 없음</TableCell>
                          )}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <FrontrunnerLearnersDialog row={scheduleRow} onClose={() => setScheduleRow(null)} />
    </div>
  )
}
